import type {
  ContentBlockId,
  ContentBlock,
  MedicationBody,
  ConditionBody,
} from "./types";
import { getBlocksByKind } from "./registry";

/**
 * Create a branded ContentBlockId from a plain string.
 * Use kebab-case IDs: "test-semen-analysis", "dp-sperm-count", etc.
 */
export function makeId(id: string): ContentBlockId {
  return id as ContentBlockId;
}

/**
 * Normalize for medication name matching: lowercase, trim, strip diacritics,
 * collapse internal whitespace. So "Ménopur" and "menopur" compare equal.
 */
function normalizeMedName(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ");
}

/**
 * Find a medication factsheet by brand name. Matches (case- and
 * diacritic-insensitive) against:
 *   1. exact: title / genericName / aliases / availability brands /
 *      brandsByCountry brands
 *   2. prefix on genericName (so "Clomiphene" finds "Clomiphene citrate"
 *      and "Estradiol" finds "Estradiol (oral)")
 * No fuzzy / typo tolerance — must be deterministic so we never silently
 * route the user to the wrong drug.
 */
export function getMedicationByBrandName(
  brandName: string,
): ContentBlock<"medication"> | undefined {
  const query = normalizeMedName(brandName);
  if (!query) return undefined;
  const allMeds = getBlocksByKind("medication");

  // Pass 1 — exact normalized match on any known name.
  const exact = allMeds.find((block) => {
    if (normalizeMedName(block.title) === query) return true;
    const body = block.body as MedicationBody;
    if (body.genericName && normalizeMedName(body.genericName) === query) {
      return true;
    }
    if (body.aliases?.some((a) => normalizeMedName(a) === query)) return true;
    if (body.availability?.some((b) => normalizeMedName(b.name) === query)) {
      return true;
    }
    return body.brandsByCountry?.some((entry) =>
      entry.brands.some((b) => normalizeMedName(b) === query),
    );
  });
  if (exact) return exact;

  // Pass 2 — prefix match on genericName, either direction. Skip very
  // short queries to avoid over-matching ("D" matching "Dexamethasone").
  if (query.length < 4) return undefined;
  return allMeds.find((block) => {
    const body = block.body as MedicationBody;
    if (!body.genericName) return false;
    const generic = normalizeMedName(body.genericName);
    return generic.startsWith(query) || query.startsWith(generic);
  });
}

/**
 * Find a supplement factsheet by name (checks title and body.name). Used as
 * a fallback when a user adds a supplement as a medication.
 */
export function getSupplementByName(
  name: string,
): ContentBlock<"supplement"> | undefined {
  const query = normalizeMedName(name);
  if (!query) return undefined;
  const all = getBlocksByKind("supplement");
  return all.find((block) => {
    if (normalizeMedName(block.title) === query) return true;
    const supplementName = (block.body as { name?: string }).name;
    if (supplementName && normalizeMedName(supplementName) === query) {
      return true;
    }
    const aliases = (block.body as { aliases?: string[] }).aliases ?? [];
    if (aliases.some((alias) => normalizeMedName(alias) === query)) {
      return true;
    }
    return false;
  });
}

/**
 * Find a condition factsheet by name (checks title and conditionName).
 */
export function getConditionByName(
  name: string,
): ContentBlock<"condition"> | undefined {
  const all = getBlocksByKind("condition");
  const lower = name.toLowerCase();
  return all.find(
    (block) =>
      block.title.toLowerCase() === lower ||
      (block.body as ConditionBody).conditionName.toLowerCase() === lower,
  );
}

/**
 * Find a procedure factsheet by name (checks title and procedureName).
 */
export function getProcedureByName(
  name: string,
): ContentBlock<"procedure"> | undefined {
  const all = getBlocksByKind("procedure");
  const lower = name.toLowerCase();
  // Exact match first
  const exact = all.find(
    (block) =>
      block.title.toLowerCase() === lower ||
      block.body.procedureName.toLowerCase() === lower,
  );
  if (exact) return exact;
  // Fallback: title or procedureName starts with the search term
  return all.find(
    (block) =>
      block.title.toLowerCase().startsWith(lower) ||
      block.body.procedureName.toLowerCase().startsWith(lower),
  );
}

/**
 * Find a test factsheet by name (checks title and testName).
 */
export function getTestByName(name: string): ContentBlock<"test"> | undefined {
  const all = getBlocksByKind("test");
  const lower = name.toLowerCase();
  return all.find(
    (block) =>
      block.title.toLowerCase() === lower ||
      block.body.testName.toLowerCase() === lower,
  );
}
